import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Settings as SettingsIcon, LogOut, Bell, ChevronRight, Sparkles, Loader2, CreditCard, Shield, Info, Eye, Trash2, AlertTriangle, Fingerprint, RefreshCw, Sun, Moon, Smartphone, Link2 } from "lucide-react";
import { BottomSheet } from "@/components/BottomSheet";
import { KinlysEyesSheet } from "@/components/KinlysEyesSheet";
import { VoicePickerCard } from "@/components/VoicePickerCard";
import { NotificationSettingsSheet } from "@/components/NotificationSettingsSheet";
import { SecuritySettingsSheet } from "@/components/SecuritySettingsSheet";
import { IntegrationsSheet } from "@/components/IntegrationsSheet";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { usePaywall } from "@/context/PaywallContext";
import { useEntitlementsRefresh } from "@/context/EntitlementsContext";
import { useTheme } from "@/context/ThemeContext";
import { isNative, restorePurchases } from "@/lib/native";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const TIER_LABEL = {
  free: "Free",
  trial: "Free trial",
  lite: "Lite",
  plus: "Plus",
  family: "Family",
  pro: "Pro",
  empire: "Empire",
};
const TIER_TONE = {
  trial: "bg-primary text-primary-foreground border-primary",
  lite: "bg-foreground text-background border-foreground",
  plus: "bg-foreground text-background border-foreground",
  family: "bg-foreground text-background border-foreground",
  pro: "bg-foreground text-background border-foreground",
  empire: "bg-foreground text-background border-foreground",
  free: "bg-secondary text-muted-foreground border-border",
};

/**
 * SettingsButton — global ⚙️ gear that mounts in every TopBar.
 * Opens the unified SettingsSheet: plan & billing (top), my vault, notifications,
 * privacy & data, about, sign out.
 *
 * Plan & billing is the CANONICAL upgrade path. Tier badge + trial countdown
 * + Upgrade CTA always visible at the top of the sheet so users can't miss it.
 */
export function SettingsButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        data-testid="topbar-settings-gear"
        onClick={() => setOpen(true)}
        aria-label="Settings"
        className="w-10 h-10 inline-flex items-center justify-center text-muted-foreground hover:text-foreground tap"
      >
        <SettingsIcon className="w-5 h-5" strokeWidth={1.6} />
      </button>
      <SettingsSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function SettingsSheet({ open, onClose }) {
  const { family, logout } = useAuth();
  const sub = useSubscription();
  const { openPaywall } = usePaywall();
  const refreshEntitlements = useEntitlementsRefresh();
  const navigate = useNavigate();
  const [showKinlysEyes, setShowKinlysEyes] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [showSecurity, setShowSecurity] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // Apple Guideline 3.1.1 requires "Restore Purchases" to be visible OUTSIDE
  // the paywall too — for users who installed on a new device and aren't
  // hitting a gated feature. Surfaces a toast on success/error.
  const onRestore = async () => {
    if (!isNative()) {
      toast.message("Restore is only available in the iOS app.");
      return;
    }
    setRestoring(true);
    try {
      await restorePurchases();
      await refreshEntitlements();
      toast.success("Purchases restored.");
    } catch (e) {
      toast.error(e?.message || "Couldn't restore purchases.");
    } finally {
      setRestoring(false);
    }
  };

  const goProfile = () => { onClose?.(); navigate("/you/profile"); };

  if (!open) return null;

  const tier = sub?.tier || "free";
  const trialDaysLeft = sub?.trial_days_left ?? 0;
  const isTrial = tier === "trial";
  const isPaid = tier !== "free" && tier !== "trial";

  return (
    <BottomSheet open={open} onClose={onClose} title="Settings" fullHeight>
      <div className="space-y-3 pb-4" data-testid="settings-sheet">

        {/* PLAN & BILLING — canonical upgrade path */}
        <article className="kinly-card border-primary/20 bg-primary/[0.04]" data-testid="settings-plan-card">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Plan</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${TIER_TONE[tier] || TIER_TONE.free}`}>
                  {TIER_LABEL[tier] || "Free"}
                </span>
                {isTrial && (
                  <span className="text-[11px] text-muted-foreground tabular-nums">
                    {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left
                  </span>
                )}
              </div>
            </div>
            {!isPaid && (
              <button
                data-testid="settings-upgrade"
                onClick={() => { onClose?.(); navigate("/subscription"); }}
                className="h-10 px-4 rounded-full bg-foreground text-background text-sm font-medium tap inline-flex items-center gap-1.5 shrink-0"
              >
                <Sparkles className="w-3.5 h-3.5" /> Upgrade
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {isTrial && trialDaysLeft > 0 && `All paid features unlocked for ${trialDaysLeft} more day${trialDaysLeft === 1 ? "" : "s"}. Founders pricing: $0.49/mo lifetime for the first 1,000 signups.`}
            {tier === "free" && "You're on Free. Upgrade to add unlimited family members, more storage, more Kinly AI requests and the recipe scanner."}
            {isPaid && "Manage billing via your App Store / Play subscription settings. Cancel anytime — your data stays."}
          </p>
        </article>

        <article className="kinly-card divide-y divide-border/70" data-testid="settings-rows">
          <SettingRow
            icon={Eye}
            label="Kinly's eyes"
            subtitle="What Kinly can see and do for you"
            onClick={() => setShowKinlysEyes(true)}
            testid="settings-row-kinlys-eyes"
          />
          <SettingRow icon={CreditCard} label="Subscription" subtitle={isPaid ? `Currently on ${sub?.tier_label || "a paid plan"}` : "Manage your Kinly plan"}
            testid="settings-row-billing"
            onClick={() => { onClose?.(); navigate("/subscription"); }} />
          <SettingRow
            icon={restoring ? Loader2 : RefreshCw}
            label="Restore purchases"
            subtitle="Re-link your subscription on this device"
            testid="settings-row-restore"
            onClick={onRestore}
            iconSpin={restoring}
          />
          <SettingRow icon={Bell} label="Notifications" subtitle="Reminders, channels, quiet hours" testid="settings-row-notifs" onClick={() => setShowNotifs(true)} />
          <SettingRow icon={Link2} label="Integrations" subtitle="Google Calendar · more soon" testid="settings-row-integrations" onClick={() => setShowIntegrations(true)} />
          <SettingRow icon={Fingerprint} label="Sign-in & security" subtitle="Face ID, Touch ID & saved sign-in" testid="settings-row-security" onClick={() => setShowSecurity(true)} />
          <SettingRow icon={Shield} label="Privacy & data" subtitle="Manage memories & forget anything in your profile" testid="settings-row-privacy" onClick={goProfile} />
          <SettingRow icon={Info} label="About" subtitle="Powered by Geminii AI" testid="settings-row-about" />
        </article>

        {/* Voice — TTS picker */}
        <VoicePickerCard />

        {/* Appearance — theme picker (moved here from /you so it lives
            with the rest of the device-level preferences). */}
        <AppearanceCard />

        {/* DANGER ZONE — required by Apple Guideline 5.1.1(v) and Google
            Play Data Safety. Sits as its own card so it's visually distinct. */}
        <article className="kinly-card border-destructive/20" data-testid="settings-danger-zone">
          <p className="text-[11px] uppercase tracking-[0.18em] text-destructive/70 mb-2">Danger zone</p>
          <button
            data-testid="settings-delete-account"
            onClick={() => setShowDelete(true)}
            className="w-full flex items-center justify-between py-2 text-left tap"
          >
            <div className="flex items-center gap-3">
              <Trash2 className="w-4 h-4 text-destructive" />
              <div>
                <p className="text-[15px] text-destructive font-medium">Delete my account</p>
                <p className="text-xs text-muted-foreground mt-0.5">Permanently removes your account and family data within 7 days.</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        </article>

        <button
          data-testid="settings-logout"
          onClick={logout}
          className="w-full h-12 rounded-xl border border-border text-destructive tap inline-flex items-center justify-center gap-2 text-sm font-medium"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      <KinlysEyesSheet open={showKinlysEyes} onClose={() => setShowKinlysEyes(false)} />
      <NotificationSettingsSheet open={showNotifs} onClose={() => setShowNotifs(false)} />
      <SecuritySettingsSheet open={showSecurity} onClose={() => setShowSecurity(false)} />
      <IntegrationsSheet open={showIntegrations} onClose={() => setShowIntegrations(false)} />
      <DeleteAccountSheet open={showDelete} onClose={() => setShowDelete(false)} />
    </BottomSheet>
  );
}


/**
 * Two-step delete confirmation sheet.
 *   Step 1: scary warning + required password input
 *   Step 2: optimistic success state + auto-redirect to /signin
 *
 * Calls `DELETE /api/auth/me` which soft-deletes the user and (if the user
 * is the family owner) the whole family workspace. Required by Apple
 * Guideline 5.1.1(v) — apps that allow account creation must allow
 * in-app account deletion.
 */
function DeleteAccountSheet({ open, onClose }) {
  const { logout, user, family } = useAuth();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const isOwner = family && user && family.owner_user_id === user.id;

  React.useEffect(() => {
    if (open) { setPassword(""); setError(""); setDone(false); setSubmitting(false); }
  }, [open]);

  const onConfirm = async () => {
    if (!password) { setError("Enter your password to confirm."); return; }
    setSubmitting(true); setError("");
    try {
      await api.delete("/auth/me", { data: { password } });
      setDone(true);
      // small delay so user reads the confirmation, then nuke local state
      setTimeout(() => { logout?.(); }, 1500);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Could not delete account.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <BottomSheet open={open} onClose={() => {}} title="Account deleted">
        <div className="p-2 text-center space-y-3" data-testid="delete-account-done">
          <Trash2 className="w-10 h-10 text-destructive mx-auto" />
          <p className="text-base">Your account has been scheduled for deletion.</p>
          <p className="text-sm text-muted-foreground">
            You'll be signed out in a moment. Hard-delete completes within 7 days per our Privacy Policy.
          </p>
        </div>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet open={open} onClose={onClose} title="Delete account?">
      <div className="space-y-4 pb-4" data-testid="delete-account-sheet">
        <article className="kinly-card border-destructive/30 bg-destructive/[0.04]">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed">
              <p className="font-medium text-destructive">This is permanent.</p>
              <p className="text-muted-foreground mt-1">
                {isOwner
                  ? "You are the owner of your family workspace. Deleting your account will also remove the whole family — events, chores, photos, chat, recipes — for everyone in it."
                  : "You'll be removed from your family. Other members will keep the workspace and shared content."}
                {" "}
                Data is soft-deleted immediately and permanently purged within 7 days. Some legal records (billing, audit) are kept per our Privacy Policy.
              </p>
            </div>
          </div>
        </article>

        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
            Confirm with your password
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="delete-account-password"
            autoComplete="current-password"
            placeholder="Your account password"
            className="mt-1.5 w-full bg-transparent border border-border rounded-xl px-3 py-2.5 text-[15px] focus:border-destructive outline-none"
          />
        </label>

        {error && <p className="text-sm text-destructive" data-testid="delete-account-error">{error}</p>}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 h-12 rounded-xl border border-border text-foreground text-sm font-medium tap"
            data-testid="delete-account-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting || !password}
            className="flex-1 h-12 rounded-xl bg-destructive text-destructive-foreground text-sm font-medium tap inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="delete-account-confirm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete forever
          </button>
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed">
          Prefer email? Write to <a className="text-primary" href="mailto:support@kinly.geminii-ai.com?subject=Delete%20my%20Kinly%20account">support@kinly.geminii-ai.com</a> from your account email. We respond within 7 business days.
        </p>
      </div>
    </BottomSheet>
  );
}

function SettingRow({ icon: Icon, label, subtitle, onClick, testid, iconSpin }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="w-full flex items-center justify-between py-3 tap text-left"
    >
      <div className="flex items-center gap-3">
        {Icon && <Icon className={`w-4 h-4 text-muted-foreground ${iconSpin ? "animate-spin" : ""}`} />}
        <div>
          <p className="text-[15px] text-foreground">{label}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </button>
  );
}


/**
 * AppearanceCard — theme picker that used to live in the You tab. Moved
 * to Settings (Batch 38) because theme is a device-level preference
 * (not personal data) and the You tab now redirects to /more anyway.
 */
function AppearanceCard() {
  const { theme, setTheme } = useTheme();
  const opts = [
    { value: "light",  label: "Light",  icon: Sun },
    { value: "dark",   label: "Dark",   icon: Moon },
    { value: "system", label: "Auto",   icon: Smartphone },
  ];
  return (
    <article className="kinly-card" data-testid="settings-appearance-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-primary/10 text-primary inline-flex items-center justify-center shrink-0">
          <Moon className="w-5 h-5" strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-[15px] font-medium">Appearance</h3>
          <p className="text-[12px] text-muted-foreground mt-0.5">
            Light, dark, or follow your phone.
          </p>
          <div role="radiogroup" aria-label="Theme" className="mt-3 grid grid-cols-3 gap-2">
            {opts.map((o) => {
              const active = theme === o.value;
              const Icon = o.icon;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  data-testid={`appearance-${o.value}`}
                  onClick={() => setTheme(o.value)}
                  className={[
                    "h-12 rounded-2xl border tap inline-flex flex-col items-center justify-center gap-0.5 transition-colors",
                    active
                      ? "border-foreground bg-foreground text-background"
                      : "border-border bg-card text-foreground",
                  ].join(" ")}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.8} />
                  <span className="text-[11px] font-medium">{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </article>
  );
}
